'use client';

import { useId, useMemo, useState, type ReactNode } from 'react';
import { ArrowDown, CircleQuestionMark } from 'lucide-react';
import type { WorkloadMetricsInterval } from '@/lib/urm-api';
import { cn } from '@/lib/utils';
import { MetricsLoadingOverlay } from '@/components/metrics-loading-overlay';
import {
  buildCombinedLatencyStabilitySummary,
  buildLatencyStabilitySummaries,
  MIN_STABILITY_ELIGIBLE_INTERVALS,
  MIN_STABILITY_MEASURED_REQUESTS,
  MIN_STABILITY_REQUESTS_PER_INTERVAL,
  RATING_ORDER,
  type LatencyStabilityRating,
  type LatencyStabilitySummary,
} from '../latency-stability.logic';

type StabilitySortKey =
  | 'rating'
  | 'typicalP95Ms'
  | 'temporalSpreadRatio'
  | 'tailSpreadRatio'
  | 'measuredRequests'
  | 'histogramCoveragePercent'
  | 'slowerThan1000Percent';

const CONFIDENCE_ORDER = { high: 0, medium: 1, low: 2 } as const;

const RATING_STYLES: Record<LatencyStabilityRating, {
  label: string;
  badge: string;
  color: string;
}> = {
  stable: {
    label: 'Stable',
    badge: 'border-emerald-600/30 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300',
    color: '#059669',
  },
  mixed: {
    label: 'Mixed',
    badge: 'border-amber-600/30 bg-amber-500/10 text-amber-700 dark:text-amber-300',
    color: '#d97706',
  },
  variable: {
    label: 'Variable',
    badge: 'border-rose-600/30 bg-rose-500/10 text-rose-700 dark:text-rose-300',
    color: '#e11d48',
  },
  insufficient: {
    label: 'Insufficient',
    badge: 'border-slate-500/30 bg-slate-500/10 text-slate-600 dark:text-slate-300',
    color: '#64748b',
  },
};

function formatLatency(value: number | null, censored = false): string {
  if (value === null) return '—';
  const formatted = value >= 1_000
    ? `${(value / 1_000).toLocaleString(undefined, { maximumFractionDigits: 2 })} s`
    : `${value.toLocaleString(undefined, { maximumFractionDigits: 1 })} ms`;
  return `${censored ? '≥' : ''}${formatted}`;
}

function formatPercent(value: number | null): string {
  if (value === null) return '—';
  if (value > 0 && value < 0.1) return '<0.1%';
  return `${value.toLocaleString(undefined, { maximumFractionDigits: 1 })}%`;
}

function formatRatio(value: number | null, lowerBound = false): string {
  if (value === null) return '—';
  return `${lowerBound ? '≥' : ''}${value.toLocaleString(undefined, { maximumFractionDigits: 2 })}×`;
}

function ratingExplanation(summary: LatencyStabilitySummary): string {
  if (summary.rating !== 'insufficient') {
    return `${RATING_STYLES[summary.rating].label} with ${summary.confidence} confidence`;
  }
  if (summary.eligibleIntervals < MIN_STABILITY_ELIGIBLE_INTERVALS) {
    return `Needs ${MIN_STABILITY_ELIGIBLE_INTERVALS} eligible intervals; has ${summary.eligibleIntervals}`;
  }
  if (summary.measuredRequests < MIN_STABILITY_MEASURED_REQUESTS) {
    return `Needs ${MIN_STABILITY_MEASURED_REQUESTS} histogram requests; has ${summary.measuredRequests}`;
  }
  if (summary.censoredIntervals > 0) {
    return 'The ≥5 s bucket prevents a reliable temporal spread estimate';
  }
  return 'Not enough usable latency observations';
}

function Sparkline({ summary }: { summary: LatencyStabilitySummary }) {
  const width = 96;
  const height = 24;
  const padding = 2;
  const values = summary.points.map((point) => point.p95Ms);
  if (values.length === 0) return <span className="text-muted-foreground">—</span>;
  const minimum = Math.min(...values);
  const maximum = Math.max(...values);
  const range = maximum - minimum;
  const coordinates = values.map((value, index) => {
    const x = values.length === 1
      ? width / 2
      : padding + index / (values.length - 1) * (width - padding * 2);
    const y = range === 0
      ? height / 2
      : height - padding - (value - minimum) / range * (height - padding * 2);
    return `${x},${y}`;
  }).join(' ');

  return (
    <svg
      role="img"
      aria-label={`${summary.label} interval p95 trend, ${values.length} eligible intervals`}
      viewBox={`0 0 ${width} ${height}`}
      className="h-6 w-24"
    >
      <polyline
        points={coordinates}
        fill="none"
        stroke={RATING_STYLES[summary.rating].color}
        strokeWidth="1.75"
        vectorEffect="non-scaling-stroke"
      />
    </svg>
  );
}

function numericSortValue(summary: LatencyStabilitySummary, key: StabilitySortKey): number {
  if (key === 'rating') return RATING_ORDER[summary.rating];
  return summary[key] ?? Number.NEGATIVE_INFINITY;
}

function ColumnHelp({ label, children }: { label: string; children: ReactNode }) {
  const tooltipId = useId();
  return (
    <span className="group relative inline-flex">
      <button
        type="button"
        aria-label={`${label} explanation`}
        aria-describedby={tooltipId}
        className="cursor-help rounded-sm outline-none focus-visible:ring-2 focus-visible:ring-ring"
      >
        <CircleQuestionMark className="h-3.5 w-3.5" aria-hidden="true" />
      </button>
      <span
        id={tooltipId}
        role="tooltip"
        className="pointer-events-none invisible absolute top-full right-0 z-50 mt-1.5 w-72 rounded-md border border-slate-700 bg-slate-950 px-2.5 py-2 text-left text-xs font-normal whitespace-normal text-white opacity-0 shadow-lg transition-opacity duration-75 group-hover:visible group-hover:opacity-100 group-focus-within:visible group-focus-within:opacity-100"
      >
        {children}
      </span>
    </span>
  );
}

function SortHeader({
  label,
  sortKey,
  selectedSortKey,
  onSelect,
  help,
  align = 'right',
}: {
  label: string;
  sortKey: StabilitySortKey;
  selectedSortKey: StabilitySortKey;
  onSelect: (key: StabilitySortKey) => void;
  help?: string;
  align?: 'left' | 'right';
}) {
  const selected = sortKey === selectedSortKey;
  return (
    <th
      scope="col"
      aria-sort={selected ? (sortKey === 'rating' ? 'ascending' : 'descending') : 'none'}
      className={cn(
        'sticky top-0 z-10 border-b bg-card px-2 py-2 font-medium text-muted-foreground',
        align === 'right' ? 'text-right' : 'text-left',
      )}
    >
      <span className={cn('inline-flex items-center gap-1', align === 'right' && 'justify-end')}>
        <button
          type="button"
          aria-label={`Sort by ${label}`}
          aria-pressed={selected}
          onClick={() => onSelect(sortKey)}
          className="inline-flex items-center gap-0.5 rounded-sm outline-none hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring"
        >
          {label}
          {selected && <ArrowDown className={cn('h-3 w-3', sortKey === 'rating' && 'rotate-180')} aria-hidden="true" />}
        </button>
        {help && <ColumnHelp label={label}>{help}</ColumnHelp>}
      </span>
    </th>
  );
}

function StabilityMetricCells({ summary }: { summary: LatencyStabilitySummary }) {
  const rating = RATING_STYLES[summary.rating];
  const p10Censored = summary.p10P95Ms !== null
    && summary.p10P95Ms >= 5_000
    && summary.censoredIntervals > 0;
  const p90Censored = summary.p90P95Ms !== null
    && summary.p90P95Ms >= 5_000
    && summary.censoredIntervals > 0;

  return (
    <>
      <td className="border-b px-2 py-1.5">
        <span
          title={ratingExplanation(summary)}
          className={cn('inline-flex rounded-full border px-2 py-0.5 text-[10px] font-medium', rating.badge)}
        >
          {rating.label}
        </span>
        {summary.rating !== 'stable' && summary.rating !== 'insufficient'
          && summary.spreadDriver !== 'both' && (
          <span
            title={summary.spreadDriver === 'median'
              ? 'The typical request (median) is what drifts over time; the slow tail holds steadier'
              : 'The slow tail (p95) is what drifts over time; the typical request holds steadier'}
            className="ml-1 text-[10px] text-muted-foreground"
          >
            · {summary.spreadDriver === 'median' ? 'median' : 'tail'}
          </span>
        )}
        <span className="ml-1 text-[10px] capitalize text-muted-foreground">{summary.confidence}</span>
      </td>
      <td className="whitespace-nowrap border-b px-2 py-1.5 text-right font-mono tabular-nums">
        {formatLatency(summary.typicalP95Ms, summary.typicalP95Censored)}
      </td>
      <td className="whitespace-nowrap border-b px-2 py-1.5 text-right font-mono tabular-nums">
        {formatLatency(summary.p10P95Ms, p10Censored)}–{formatLatency(summary.p90P95Ms, p90Censored)}
      </td>
      <td className="whitespace-nowrap border-b px-2 py-1.5 text-right font-mono tabular-nums">
        {formatRatio(summary.temporalSpreadRatio, summary.temporalSpreadIsLowerBound)}
      </td>
      <td
        title={`Tail spread ${formatRatio(summary.tailSpreadRatio, summary.windowP95Censored)}`}
        className="whitespace-nowrap border-b px-2 py-1.5 text-right font-mono tabular-nums"
      >
        {formatLatency(summary.windowP50Ms)}→{formatLatency(summary.windowP95Ms, summary.windowP95Censored)}
      </td>
      <td
        title={`${summary.measuredRequests.toLocaleString()} of ${summary.totalRequests.toLocaleString()} requests have histograms`}
        className="border-b px-2 py-1.5 text-right font-mono tabular-nums"
      >
        {summary.measuredRequests.toLocaleString()}
      </td>
      <td
        title={`An interval is eligible at ${MIN_STABILITY_REQUESTS_PER_INTERVAL}+ histogram requests`}
        className="border-b px-2 py-1.5 text-right font-mono tabular-nums"
      >
        {summary.eligibleIntervals}/{summary.activeIntervals}
      </td>
      <td
        title={`${formatPercent(summary.eligibleIntervalCoveragePercent)} eligible interval coverage; ${formatPercent(summary.histogramCoveragePercent)} request coverage`}
        className="border-b px-2 py-1.5 text-right font-mono tabular-nums"
      >
        {formatPercent(summary.histogramCoveragePercent)}
      </td>
      <td className="border-b px-2 py-1.5 text-right font-mono tabular-nums">
        {formatPercent(summary.slowerThan1000Percent)}
      </td>
      <td className="border-b px-2 py-1.5"><Sparkline summary={summary} /></td>
    </>
  );
}

export function LatencyStabilityOverview({
  intervals,
  loading,
}: {
  intervals: WorkloadMetricsInterval[];
  loading: boolean;
}) {
  const [sortKey, setSortKey] = useState<StabilitySortKey>('rating');
  const [selectedRowKeys, setSelectedRowKeys] = useState<Set<string>>(() => new Set());
  const summaries = useMemo(() => buildLatencyStabilitySummaries(intervals), [intervals]);
  const activeSelectedKeys = useMemo(() => {
    const availableKeys = new Set(summaries.map((summary) => summary.key));
    return new Set(Array.from(selectedRowKeys).filter((key) => availableKeys.has(key)));
  }, [selectedRowKeys, summaries]);
  const combinedSummary = useMemo(
    () => buildCombinedLatencyStabilitySummary(intervals, activeSelectedKeys),
    [activeSelectedKeys, intervals],
  );
  const sortedSummaries = useMemo(() => [...summaries].sort((left, right) => {
    const leftValue = numericSortValue(left, sortKey);
    const rightValue = numericSortValue(right, sortKey);
    const difference = sortKey === 'rating' ? leftValue - rightValue : rightValue - leftValue;
    if (difference !== 0) return difference;
    if (sortKey === 'rating') {
      const confidenceDifference = CONFIDENCE_ORDER[left.confidence] - CONFIDENCE_ORDER[right.confidence];
      if (confidenceDifference !== 0) return confidenceDifference;
      const spreadDifference = (left.temporalSpreadRatio ?? Number.POSITIVE_INFINITY)
        - (right.temporalSpreadRatio ?? Number.POSITIVE_INFINITY);
      if (spreadDifference !== 0) return spreadDifference;
      if (left.measuredRequests !== right.measuredRequests) {
        return right.measuredRequests - left.measuredRequests;
      }
    }
    return left.label.localeCompare(right.label);
  }), [sortKey, summaries]);
  const allRowsSelected = summaries.length > 0 && activeSelectedKeys.size === summaries.length;
  const toggleRow = (key: string) => {
    setSelectedRowKeys((current) => {
      const next = new Set(current);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };

  return (
    <section
      aria-label="Latency stability"
      className="space-y-2 rounded-xl border border-primary/20 bg-muted/40 p-2.5 shadow-sm"
    >
      <div className="min-w-0 rounded-lg border bg-card px-3 pt-3 pb-2 shadow-sm">
        <div className="mb-2 flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
          <h3 className="text-sm font-semibold">API latency stability</h3>
          <p className="text-xs text-muted-foreground">
            Per-interval p95 variation by ResourceType × Action × APILabel. Select rows to calculate their combined stability.
          </p>
        </div>
        <MetricsLoadingOverlay loading={loading}>
          {summaries.length === 0 && !loading ? (
            <div className="p-8 text-center text-sm text-muted-foreground">No workload metrics were recorded in this window.</div>
          ) : (
            <>
              <div className="max-h-[720px] overflow-auto">
                  <table aria-label="API latency stability details" className="w-full min-w-[1420px] table-fixed border-separate border-spacing-0 text-xs">
                    <colgroup>
                      <col className="w-[3%]" />
                      <col className="w-[10%]" />
                      <col className="w-[6%]" />
                      <col className="w-[10%]" />
                      <col className="w-[8%]" />
                      <col className="w-[7%]" />
                      <col className="w-[9%]" />
                      <col className="w-[7%]" />
                      <col className="w-[9%]" />
                      <col className="w-[8%]" />
                      <col className="w-[8%]" />
                      <col className="w-[8%]" />
                      <col className="w-[7%]" />
                      <col className="w-[8%]" />
                    </colgroup>
                    <thead>
                      <tr>
                        <th scope="col" className="sticky top-0 z-10 border-b bg-card px-2 py-2 text-center font-medium text-muted-foreground">
                          <input
                            type="checkbox"
                            checked={allRowsSelected}
                            aria-label={allRowsSelected ? 'Clear all API row selections' : 'Select all API rows'}
                            title={allRowsSelected ? 'Clear selection' : 'Select all rows'}
                            onChange={() => setSelectedRowKeys(
                              allRowsSelected
                                ? new Set()
                                : new Set(summaries.map((summary) => summary.key)),
                            )}
                            className="h-3.5 w-3.5 accent-primary"
                          />
                        </th>
                        <th scope="col" className="sticky top-0 z-10 border-b bg-card px-2 py-2 text-left font-medium text-muted-foreground">ResourceType</th>
                        <th scope="col" className="sticky top-0 z-10 border-b bg-card px-2 py-2 text-left font-medium text-muted-foreground">Action</th>
                        <th scope="col" className="sticky top-0 z-10 border-b bg-card px-2 py-2 text-left font-medium text-muted-foreground">APILabel</th>
                        <SortHeader
                          label="Stability" sortKey="rating" selectedSortKey={sortKey}
                          onSelect={setSortKey} align="left"
                          help={`Whether this API's whole latency profile stays consistent over time or jumps around. An API is only as stable as its least steady percentile, so both the typical request (median) and the slow tail (p95) are checked — the tag shows which one drifts. Technically: p90 ÷ p10 of the interval p50 and of the interval p95 — stable ≤1.25×, mixed ≤2×, variable >2×. Classification needs ${MIN_STABILITY_ELIGIBLE_INTERVALS} eligible intervals and ${MIN_STABILITY_MEASURED_REQUESTS} histogram requests. Confidence also reflects coverage and collection drops.`}
                        />
                        <SortHeader
                          label="Typical p95" sortKey="typicalP95Ms" selectedSortKey={sortKey}
                          onSelect={setSortKey}
                          help="A representative slow-request latency for this API, with every time interval counted equally. Technically: the median of the API's eligible interval-level p95 values."
                        />
                        <th scope="col" className="sticky top-0 z-10 border-b bg-card px-2 py-2 text-right font-medium text-muted-foreground">
                          <span className="inline-flex items-center justify-end gap-1">
                            p10–p90 p95
                            <ColumnHelp label="p10–p90 p95">The range this API&apos;s slow-request latency moves within from one interval to the next — a narrow range means consistent latency over time. Technically: the middle 80% (p10–p90) of eligible interval-level p95 values.</ColumnHelp>
                          </span>
                        </th>
                        <SortHeader
                          label="Spread" sortKey="temporalSpreadRatio" selectedSortKey={sortKey}
                          onSelect={setSortKey}
                          help="How much this API's slow-request latency swings over time — lower is steadier, 1× means no measured change. Technically: p90 interval p95 ÷ p10 interval p95."
                        />
                        <SortHeader
                          label="Window p50→p95" sortKey="tailSpreadRatio" selectedSortKey={sortKey}
                          onSelect={setSortKey}
                          help="Within a typical slice of time, how much slower a slow request is than a normal one. Technically: request-level p50 and p95 from the histogram summed over the whole window — this compares typical vs slow requests, not time intervals like Spread does."
                        />
                        <SortHeader
                          label="Histogram requests" sortKey="measuredRequests" selectedSortKey={sortKey}
                          onSelect={setSortKey}
                          help="How many requests we have detailed timing for, which drives how trustworthy these numbers are. Technically: total requests represented in latency histogram buckets — this can be lower than total requests when older or partial records have no histogram."
                        />
                        <th scope="col" className="sticky top-0 z-10 border-b bg-card px-2 py-2 text-right font-medium text-muted-foreground">
                          <span className="inline-flex items-center justify-end gap-1">
                            Eligible / active
                            <ColumnHelp label="Eligible / active">How many of the intervals with traffic had enough requests to measure latency reliably. Technically: active intervals contain requests for this API; eligible intervals also contain at least {MIN_STABILITY_REQUESTS_PER_INTERVAL} histogram requests, enough to estimate interval p95.</ColumnHelp>
                          </span>
                        </th>
                        <SortHeader
                          label="Histogram coverage" sortKey="histogramCoveragePercent" selectedSortKey={sortKey}
                          onSelect={setSortKey}
                          help="How much of this API's traffic came with detailed timing data — higher means these latency numbers represent more of what actually happened. Technically: percentage of the API's total requests represented by latency histograms in the selected window."
                        />
                        <SortHeader
                          label="≥1 second" sortKey="slowerThan1000Percent" selectedSortKey={sortKey}
                          onSelect={setSortKey}
                          help="How often this API takes a full second or more to respond. Technically: percentage of histogram-measured requests in buckets whose latency is at least one second."
                        />
                        <th scope="col" className="sticky top-0 z-10 border-b bg-card px-2 py-2 text-left font-medium text-muted-foreground">
                          <span className="inline-flex items-center gap-1">
                            p95 trend
                            <ColumnHelp label="p95 trend">A quick visual of how this API&apos;s slow-request latency moved over the window. Technically: interval-level p95 values in chronological order; intervals below the minimum request count are omitted.</ColumnHelp>
                          </span>
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {combinedSummary && (
                        <tr className="bg-primary/5">
                          <td className="border-b px-2 py-1.5 text-center">
                            <input
                              type="checkbox"
                              checked
                              aria-label="Clear combined selection"
                              title="Clear selection"
                              onChange={() => setSelectedRowKeys(new Set())}
                              className="h-3.5 w-3.5 accent-primary"
                            />
                          </td>
                          <th scope="row" colSpan={3} className="border-b px-2 py-1.5 text-left font-semibold text-primary">
                            {combinedSummary.label}
                          </th>
                          <StabilityMetricCells summary={combinedSummary} />
                        </tr>
                      )}
                      {sortedSummaries.map((summary) => (
                          <tr key={summary.key} className={activeSelectedKeys.has(summary.key) ? 'bg-primary/[0.03]' : undefined}>
                            <td className="border-b px-2 py-1.5 text-center">
                              <input
                                type="checkbox"
                                checked={activeSelectedKeys.has(summary.key)}
                                aria-label={`Select ${summary.label}`}
                                onChange={() => toggleRow(summary.key)}
                                className="h-3.5 w-3.5 accent-primary"
                              />
                            </td>
                            <td title={summary.resourceType} className="truncate border-b px-2 py-1.5 font-mono">{summary.resourceType}</td>
                            <td title={summary.action} className="truncate border-b px-2 py-1.5 font-mono">{summary.action}</td>
                            <td title={summary.apiLabel} className="truncate border-b px-2 py-1.5 font-mono">{summary.apiLabel}</td>
                            <StabilityMetricCells summary={summary} />
                          </tr>
                      ))}
                    </tbody>
                  </table>
              </div>
              <p className="mt-2 text-[10px] text-muted-foreground">
                Stable ≤1.25×, mixed ≤2×, variable &gt;2×, applied to both the median and the p95 over time; the overall rating is the less stable of the two. Classification needs {MIN_STABILITY_ELIGIBLE_INTERVALS} eligible intervals and {MIN_STABILITY_MEASURED_REQUESTS} histogram requests. Values in the open-ended ≥5 s bucket are shown as lower bounds.
              </p>
            </>
          )}
        </MetricsLoadingOverlay>
      </div>
    </section>
  );
}
